import { useState, useEffect, useCallback } from "react";
import { useNotifications } from "../../context/NotificationContext";
import { fetchPendingRequests, approveAccessRequest, rejectAccessRequest, AgentAccessRequestDto } from "../../../api/accessRequests";
import "./AccessRequestsPage.css";

type StatusFilter = "All" | "Pending" | "Approved" | "Rejected";

export function AccessRequestsPage() {
  const { refreshPendingRequests } = useNotifications();
  const [filter, setFilter] = useState<StatusFilter>("Pending");
  const [requests, setRequests] = useState<AgentAccessRequestDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const loadRequests = useCallback(async (statusFilter: StatusFilter) => {
    setLoading(true);
    try {
      const data = await fetchPendingRequests(statusFilter === "All" ? undefined : statusFilter);
      setRequests(data);
    } catch (err) {
      console.error("Failed to load access requests:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests(filter);
  }, [filter, loadRequests]);

  const handleApprove = async (requestId: string) => {
    setProcessingId(requestId);
    try {
      await approveAccessRequest(requestId);
      await Promise.all([loadRequests(filter), refreshPendingRequests()]);
    } catch (err) {
      console.error("Failed to approve request:", err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    setProcessingId(requestId);
    try {
      await rejectAccessRequest(requestId);
      await Promise.all([loadRequests(filter), refreshPendingRequests()]);
    } catch (err) {
      console.error("Failed to reject request:", err);
    } finally {
      setProcessingId(null);
    }
  };

  const filteredRequests = requests;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const filters: StatusFilter[] = ["All", "Pending", "Approved", "Rejected"];

  return (
    <div className="access-requests-page">
      <h1>Access Requests</h1>
      <p className="access-requests-subtitle">Review and manage agent access requests from users</p>

      <div className="access-requests-filters">
        {filters.map((f) => (
          <button
            key={f}
            className={`filter-tab ${filter === f ? "active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="access-requests-empty">
          <p>Loading...</p>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="access-requests-empty">
          <p>No {filter === "All" ? "" : filter.toLowerCase()} requests found</p>
        </div>
      ) : (
        <table className="access-requests-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Agent</th>
              <th>Requested</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((req) => (
              <tr key={req.requestId}>
                <td>
                  <div className="user-cell">
                    <span className="user-cell-name">{req.userName}</span>
                    <span className="user-cell-email">{req.userEmail}</span>
                  </div>
                </td>
                <td>{req.agentName}</td>
                <td>{formatDate(req.requestedAt)}</td>
                <td>
                  <span className={`status-badge ${req.status.toLowerCase()}`}>
                    {req.status}
                  </span>
                </td>
                <td>
                  {req.status === "Pending" ? (
                    <div className="request-actions">
                      <button
                        className="btn-approve"
                        disabled={processingId === req.requestId}
                        onClick={() => handleApprove(req.requestId)}
                      >
                        Approve
                      </button>
                      <button
                        className="btn-reject"
                        disabled={processingId === req.requestId}
                        onClick={() => handleReject(req.requestId)}
                      >
                        Reject
                      </button>
                    </div>
                  ) : (
                    <span style={{ color: "#9e9e9e", fontSize: 12 }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
